import { Injectable } from '@angular/core';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { IAuthProvider, IdentityClaims, IdentityProvider } from '~models';

@Injectable({
  providedIn: 'root',
})
export class IDS4Service implements IAuthProvider {
  private user: IdentityClaims;
  public readonly identityProvider = IdentityProvider.IdentityServer4;

  constructor(private oauthService: OAuthService, private cookieService: CookieService) {
    this.oauthService.configure({
      issuer: environment.zipIdp.issuer,
      redirectUri: `${window.location.origin}/au/giftcards/auth/callback`,
      clientId: environment.zipIdp.clientId,
      responseType: 'code',
      scope: 'openid profile offline_access',
      useSilentRefresh: true,
      silentRefreshRedirectUri: `${window.location.origin}/au/giftcards/silent-refresh.html`,
    });
  }

  public async loadDiscoveryDocument() {
    await this.oauthService.loadDiscoveryDocument();
  }

  public async afterAuth() {
    this.user = this.oauthService.getIdentityClaims() as IdentityClaims;
    this.oauthService.setupAutomaticSilentRefresh();
  }

  public async getAccessToken() {
    return this.oauthService.getAccessToken();
  }

  public async handleCallback() {
    try {
      await this.oauthService.tryLoginCodeFlow();
      const returnTo = decodeURIComponent(this.oauthService.state);
      return returnTo ?? '/';
    } catch (error) {
      window.zbm.reportCustomError(
        'IDS4_CALLBACK_FAILED',
        error?.reason?.params?.error ?? error?.type,
        this.cookieService.get('zip_id'),
      );
      throw error;
    }
  }

  public get customerId(): string {
    return this.user?.customer_id ?? this.user?.sub;
  }

  public async redirectToLogin(returnTo: string) {
    this.oauthService.initCodeFlow(returnTo);
  }

  public async silentRefresh() {
    try {
      await this.oauthService.silentRefresh();
      return true;
    } catch (error) {
      // @ts-ignore error message exist in OAuthErrorEvent
      if (error instanceof OAuthErrorEvent && error?.reason?.params?.error !== 'login_required') {
        window.zbm.reportCustomError(
          'IDS4_SILENT_REFRESH_FAILED',
          // @ts-ignore error message exist in OAuthErrorEvent
          error?.reason?.params?.error ?? error?.type,
          this.cookieService.get('zip_id'),
        );
      }
      return false;
    }
  }

  public async isAuthenticated() {
    return this.oauthService.hasValidAccessToken();
  }
}
